import { useNavigate } from "react-router-dom";
import { useNotifications } from "features/notifications/hooks";
import { useTeamNameSelector } from "pages/team_settings/hooks";
import {
  useCreateGaiaObjectMutation,
  useUpdateGaiaObjectMutation,
} from "../endpoints";
import { useUpdateScanJobMutation } from "pages/drafts/endpoint";
import {
  useUploadUrlMutation,
  useBatchRegisterDocumentsMutation,
} from "components/documents-table-os/endpoints";
import {
  BatchRegisterDocumentsInput,
  UploadUrlRequest,
  UploadUrlResponse,
} from "components/documents-table-os/definitions";
import {
  CreateGaiaObjectResponse,
  UpdateGaiaObjectRequest,
} from "../definitions";
import { GaiaObject } from "models/generateGaiaObjSchemas";
import { registerScanDocument, updateScanStatus } from "utils/scan-job-utils";
import { getGaiaObjectErrorMessage } from "../util";
import {
  cap,
  getFileExtension,
  getFileNameWithoutExtension,
  getFileSize,
  pluralize,
} from "utils/date-format";
import { SubmissionContext } from "../adapters/definitions";

/**
 * Hook for creating Gaia objects
 * Handles the complete create flow including success/error handling and navigation
 */
export const useCreateGaiaObject = () => {
  const [createGaiaObjectMutation] = useCreateGaiaObjectMutation();
  const { addNotification } = useNotifications();
  const navigate = useNavigate();

  const createGaiaObject = async (
    gaiaObjectData: GaiaObject,
    context: SubmissionContext,
  ): Promise<CreateGaiaObjectResponse> => {
    const { metadata, ui, adapter } = context;
    const defaultErrorMessage = `Failed to create the ${metadata.type}`;

    try {
      const response = await createGaiaObjectMutation(gaiaObjectData).unwrap();

      addNotification({
        type: "success",
        header: `${cap(metadata.type)} created successfully`,
        dismissible: true,
        timeout: 10000,
      });

      const successPath = adapter.getSuccessRedirectPath(
        metadata.type,
        metadata.shortId,
      );
      navigate(successPath);

      return response;
    } catch (e: any) {
      const errorMessage = getGaiaObjectErrorMessage(
        e,
        metadata.type,
        defaultErrorMessage,
      );

      addNotification({
        type: "error",
        header: errorMessage,
        dismissible: true,
        timeout: 10000,
      });

      if (ui.setDisableCreateEdit) {
        ui.setDisableCreateEdit(false);
      }
      throw e;
    }
  };

  return { createGaiaObject };
};

/**
 * Hook for updating Gaia objects
 * Handles the complete update flow including success/error handling and navigation
 */
export const useUpdateGaiaObject = () => {
  const [updateGaiaObjectMutation] = useUpdateGaiaObjectMutation();
  const { addNotification } = useNotifications();
  const navigate = useNavigate();

  const updateGaiaObject = async (
    updateRequest: UpdateGaiaObjectRequest,
    context: SubmissionContext,
  ): Promise<GaiaObject> => {
    const { metadata, ui, adapter } = context;
    const defaultErrorMessage = `Failed to update the ${metadata.type}`;

    try {
      const response = await updateGaiaObjectMutation(updateRequest).unwrap();

      addNotification({
        type: "success",
        header: `${cap(metadata.type)} updated successfully`,
        dismissible: true,
        timeout: 10000,
      });

      const successPath = adapter.getSuccessRedirectPath(
        metadata.type,
        metadata.shortId,
      );
      navigate(successPath);

      return response;
    } catch (e: any) {
      const errorMessage = getGaiaObjectErrorMessage(
        e,
        metadata.type,
        defaultErrorMessage,
      );

      addNotification({
        type: "error",
        header: errorMessage,
        dismissible: true,
        timeout: 10000,
      });

      if (ui.setDisableCreateEdit) {
        ui.setDisableCreateEdit(false);
      }
      throw e;
    }
  };

  return { updateGaiaObject };
};

/**
 * Hook for document upload operations
 * Handles file upload and document registration
 */
export const useDocumentUpload = () => {
  const [uploadUrlMutation] = useUploadUrlMutation();
  const [batchRegisterDocumentsMutation] = useBatchRegisterDocumentsMutation();
  const { teamName } = useTeamNameSelector();

  const uploadDocument = async (
    file: File,
    gaiaObjectResponse: CreateGaiaObjectResponse,
    context: SubmissionContext,
    documentType: string,
  ): Promise<void> => {
    const { metadata } = context;

    const request: UploadUrlRequest = {
      prefix: `${pluralize(metadata.type)}/${gaiaObjectResponse.shortId}`,
      fileName: file.name,
      teamName: teamName,
      securityLevel: "Normal",
    };

    const response: UploadUrlResponse =
      await uploadUrlMutation(request).unwrap();

    await fetch(response.url, {
      method: "PUT",
      body: file,
      headers: {
        "x-amz-expected-bucket-owner": response.bucketOwner,
      },
    });

    const s3Key = `${pluralize(metadata.type)}/${gaiaObjectResponse.shortId}/${
      response.uniqueKey
    }/${file.name}`;

    const registerRequest: BatchRegisterDocumentsInput = {
      documents: [
        {
          parentId: gaiaObjectResponse.shortId,
          gaiaObjectId: gaiaObjectResponse.shortId,
          teamName: teamName,
          fileName: getFileNameWithoutExtension(file.name),
          documentType,
          securityLevel: "Normal",
          s3Key,
          extensionType: getFileExtension(file.name),
          size: getFileSize(file.size),
        },
      ],
    };

    await batchRegisterDocumentsMutation(registerRequest).unwrap();
  };

  return { uploadDocument };
};

/**
 * Hook for draft-specific operations
 * Handles scan document registration and status updates for draft mode
 */
export const useDraftOperations = () => {
  const [batchRegisterDocumentsMutation] = useBatchRegisterDocumentsMutation();
  const [updateScanJobMutation] = useUpdateScanJobMutation();
  const { teamName } = useTeamNameSelector();

  const processDraftOperations = async (
    responseShortId: string,
    context: SubmissionContext,
  ): Promise<void> => {
    const { metadata, formData } = context;

    await registerScanDocument(
      metadata.shortId!,
      formData.scanJobData,
      responseShortId,
      teamName,
      batchRegisterDocumentsMutation,
      metadata.documentType,
    );

    await updateScanStatus(
      metadata.shortId!,
      responseShortId,
      updateScanJobMutation,
    );
  };

  return { processDraftOperations };
};
